import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button, Text } from "@pentabd/ui";

import {
  InputTextAreaRow,
  InputTextRow,
  SelectControllerRow,
} from "@components";

import { useLanguage } from "@hooks/useLanguage";
import { useMaintenance } from "@hooks/inventory/maintenance/useMaintenance";
import { useUser } from "@hooks/useUser";
import { useMetaData } from "@hooks/inventory/settings/useMetaData";
import { useEmployeeMetaData } from "@hooks/inventory/settings/useMetaData";

import { getConvertedMetaDataForEmployeeAsset } from "@helpers/inventory/metaDataConvert";

import {
  MAINTENANCE_VALIDATION,
  MaintenanceValidationSchemaType,
  maintenanceValidationSchema,
} from "@validations/Inventory/maintenance/maintenance";
import { CreateMaintenanceType } from "@type/inventory/maintenance";

import { SETTINGS_META_DATA } from "@constants/inventory/settings-meta-data";

import { ROUTES } from "@constants/routes";
import { useEffect, useState } from "react";

const { CATEGORY, GROUP, BRAND, NAME } = SETTINGS_META_DATA;

const { INVENTORY } = ROUTES;
const { MAINTENANCE } = INVENTORY;

export default function CreateMaintenanceForm() {
  const [inventoryType, setInventoryType] = useState<string>("");

  const { t } = useTranslation();
  const { language } = useLanguage();

  const navigate = useNavigate();

  const { user } = useUser();
  const { employeeMasterId } = user || {};

  const moduleName = localStorage.getItem("module");
  const module = moduleName === "ITS" ? "ICT" : "NON_ICT";

  const { data: categories } = useMetaData(CATEGORY);
  const { data: groups } = useMetaData(GROUP);
  const { data: brands } = useMetaData(BRAND);
  const { data: names } = useMetaData(NAME);
  const { data: employeeAsstes } = useEmployeeMetaData(
    employeeMasterId,
    module,
  );

  const { createMaintenance, maintenanceListState } = useMaintenance();

  const method = useForm<MaintenanceValidationSchemaType>({
    resolver: yupResolver(maintenanceValidationSchema),
  });
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = method;

  const { isCreateLoading } = maintenanceListState;

  const onSubmit: SubmitHandler<MaintenanceValidationSchemaType> = (
    data: MaintenanceValidationSchemaType,
  ) => {
    const __data: any = {
      ...data,
      // categoryId: Number(data.categoryId),
      // groupId: Number(data.groupId),
      // brandId: Number(data.brandId),
      assetItemId: Number(data.assetItemId),
      employeeId: Number(user.employeeMasterId),
      inventoryType,
      //!!!
      bookStartDate: null,
      bookEndDate: null,
      status: null,
    };

    createMaintenance(__data as CreateMaintenanceType);
  };

  const cancelHandler = () => {
    navigate(MAINTENANCE);
  };

  useEffect(() => {
    setInventoryType(module);
  }, [module]);

  return (
    <div className="container p-9 g-10">
      <FormProvider {...method}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex flex-column gap-8 pb-8">
            <SelectControllerRow
              label="INVENTORY_MAINTENANCE.FORM.NAME"
              control={control}
              name={MAINTENANCE_VALIDATION.NAME}
              register={{ ...register(MAINTENANCE_VALIDATION.NAME) }}
              options={getConvertedMetaDataForEmployeeAsset(
                employeeAsstes,
                language,
              )}
              setValue={setValue}
              required
              error={errors as any}
            />
            <InputTextRow
              register={{ ...register(MAINTENANCE_VALIDATION.ISSUE_NAME) }}
              label="INVENTORY_MAINTENANCE.FORM.ISSUE_NAME"
              error={errors as any}
              required
            />
            <InputTextAreaRow
              register={{ ...register(MAINTENANCE_VALIDATION.ISSUE_DETAILS) }}
              label="INVENTORY_MAINTENANCE.FORM.ISSUE_DETAILS"
              error={errors as any}
            />

            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title"></Text>
              </div>
              <div className="col-md-12 col-lg-7">
                <div className="d-flex justify-content-end gap-8">
                  <Button
                    fill="outline"
                    type="light"
                    size="md"
                    onClick={cancelHandler}
                  >
                    {t("INVENTORY_MAINTENANCE.FORM.CANCEL_BUTTON_TEXT")}
                  </Button>
                  <Button
                    fill="fill"
                    type="info"
                    size="md"
                    htmlType="submit"
                    loading={isCreateLoading}
                  >
                    {t("INVENTORY_MAINTENANCE.FORM.SAVE_BUTTON_TEXT")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
